import React, { FC, useMemo, useState } from 'react'

import {
  Column,
  FilterSidebarData,
  getListFromEdgesList,
  Loader,
  parseFilterItems
} from '../../components'
import { useApi } from '../../providers'
import { ProductView, reduceProductsToProductList } from '../../views'
import {
  ProductFilter,
  ProductFiltersParseJSON,
  ProductOrderSort,
  ProductsByCategoryIdVariables
} from '../../services'

import { useStyle } from './products.styles'
import { ProductsViewPageProps } from './products-view.types'

export const ProductsViewPage: FC<ProductsViewPageProps> = ({
  category,
  vendorId
}) => {
  const { id } = category
  const {
    product: { useProductsByCategoryId }
  } = useApi()
  const first = 8

  const [productVariables, changeProductVariables] =
    useState<ProductsByCategoryIdVariables>({
      id,
      first,
      filter: vendorId ? { vendors: [vendorId] } : undefined
    })

  const {
    data: response,
    loading,
    refetch,
    fetchMore
  } = useProductsByCategoryId(productVariables)

  const classes = useStyle()

  const Data = useMemo(() => {
    if (response) {
      const products = getListFromEdgesList(response.category.products)
      return reduceProductsToProductList(products)
    }

    return []
  }, [response])

  const TotalCount = useMemo(
    () => response?.category.products.totalCount || 0,
    [response?.category.products.totalCount]
  )

  const Name = useMemo(
    () => response?.category.name || '',
    [response?.category.name]
  )

  const handleOnSort = (sortBy?: ProductOrderSort) => {
    refetch({ ...productVariables, first, sortBy })
    changeProductVariables({ ...productVariables, first, sortBy })
  }

  const handleOnLoadNext = (filter: ProductFilter) => {
    if (fetchMore && response) {
      const productEdges = response.category.products.edges
      const lastIndex = productEdges.length - 1
      fetchMore({
        variables: {
          id,
          first,
          filter,
          after: productEdges[lastIndex].cursor
        }
      })
    }
  }

  const handleOnChangeFilter = (value: ProductFilter) => {
    if (refetch) {
      refetch({
        id,
        first,
        filter: value
      })
    }
  }

  const filtersData = response ? response.category.products.filters : undefined
  const Filters = useMemo(() => {
    if (filtersData) {
      const list = parseFilterItems<ProductFiltersParseJSON>(filtersData)

      if (list) {
        return list
      }
    }

    return {
      colors: [],
      materials: [],
      vendors: [],
      genders: [],
      priceRange: [],
      companyNames: [],
      productTypes: [],
      sizes: [],
      rating: [],
      productionDaysRange: []
    } as FilterSidebarData<ProductFiltersParseJSON>
  }, [filtersData])

  if (loading) {
    return (
      <Column className={classes.loadingContainer}>
        <Loader width={40} height={40} />
      </Column>
    )
  }

  return (
    <ProductView
      id={id}
      loading={loading}
      category={category}
      data={Data}
      title={Name}
      totalCount={TotalCount}
      filtersData={Filters}
      onLoadNext={handleOnLoadNext}
      onChangeFilter={handleOnChangeFilter}
      onSort={handleOnSort}
    />
  )
}
